import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { formatScheduleForDisplay } from 'src/helpers/scheduler-schedule.helpers';
import type { RegisteredSchedulerTask, ScheduledTask } from '../types/scheduler.types';
import { SchedulerRunHistory } from './scheduler-run-history';
import { SchedulerTaskForm, type SchedulerTaskFormValues } from './scheduler-task-form';

export type SchedulerTaskSheetMode = 'create' | 'edit';

interface SchedulerTaskSheetProps {
  open: boolean;
  mode: SchedulerTaskSheetMode;
  task: ScheduledTask | null;
  registry: RegisteredSchedulerTask[];
  isSaving?: boolean;
  submitError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: SchedulerTaskFormValues) => void | Promise<void>;
}

export function SchedulerTaskSheet({
  open,
  mode,
  task,
  registry,
  isSaving = false,
  submitError = null,
  onOpenChange,
  onSubmit,
}: SchedulerTaskSheetProps) {
  const isCreate = mode === 'create';

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-xl"
      >
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New scheduled task' : task?.name ?? 'Task details'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              {isCreate
                ? 'Pick a registered task and schedule it with a daily time or cron expression.'
                : 'Update the schedule or review recent runs for this task.'}
            </SheetDescription>
          </SheetHeader>
          {!isCreate && task ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Next run
                </dt>
                <dd className="mt-0.5 text-black">
                  {task.next_run_at ? formatDisplayDateTime(task.next_run_at) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Schedule
                </dt>
                <dd className="mt-0.5 font-mono text-sm text-black">
                  {formatScheduleForDisplay(task)}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <SchedulerTaskForm
            registry={registry}
            initialTask={isCreate ? null : task}
            isSaving={isSaving}
            submitError={submitError}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />

          {!isCreate && task ? (
            <section
              className="mt-8 border-t border-gray-300 pt-6"
              aria-labelledby="scheduler-run-history-heading"
            >
              <h3
                id="scheduler-run-history-heading"
                className="text-sm font-medium uppercase tracking-wide text-gray-500"
              >
                Run history
              </h3>
              <div className="mt-4">
                <SchedulerRunHistory task={task} />
              </div>
            </section>
          ) : null}
        </div>

        {isSaving ? (
          <div
            className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white"
            role="status"
          >
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
